import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useChildAuthStore } from '@/stores/childAuth';
import { useUserContextStore } from '@/stores/userContext';
import { useAppContextStore } from '@/stores/appContext';
import { perfMark, perfIncrement, perfLogSummary, perfLogConclusion } from '@/utils/perf-metrics';
import { isTabProbeEnabled } from '@/utils/tab-switch-probe';
import { resolveGoTarget } from '@/services/go-deeplink';
import { installPageTransitions } from '@/composables/usePageTransition';
import { HOME_PATH } from '@/router/paths';
import { decideChildModeLock } from '@/router/childModeLock';

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    redirect: '/welcome'
  },
  {
    path: '/welcome',
    component: () => import('@/views/onboarding/OnboardingWelcomePage.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/onboarding/name',
    component: () => import('@/views/onboarding/OnboardingNamePage.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/onboarding/children',
    component: () => import('@/views/onboarding/OnboardingChildrenPage.vue'),
    meta: { requiresAuth: false },
  },
  {
    // Écran fusionné (design Soleil) : les anciens brouillons persistés à
    // l'étape « children-generated » retombent sur /onboarding/children.
    path: '/onboarding/children-generated',
    redirect: '/onboarding/children',
  },
  {
    path: '/onboarding/tasks',
    component: () => import('@/views/onboarding/OnboardingTasksPage.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/onboarding/rewards',
    component: () => import('@/views/onboarding/OnboardingRewardsPage.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/onboarding/account',
    component: () => import('@/views/onboarding/OnboardingAccountPage.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/onboarding/guided-validation',
    component: () => import('@/views/onboarding/OnboardingGuidedValidationPage.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/onboarding/founders',
    component: () => import('@/views/onboarding/OnboardingFoundersPage.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/onboarding/success',
    component: () => import('@/views/onboarding/OnboardingSuccessPage.vue'),
    meta: { requiresAuth: false },
  },

  {
    path: '/login',
    component: () => import('@/views/auth/LoginPage.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/auth/reset-password',
    component: () => import('@/views/auth/ResetPasswordPage.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/verify-email',
    component: () => import('@/views/auth/VerifyEmailPage.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/auth/verify-email',
    component: () => import('@/views/auth/VerifyEmailPage.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/auth/callback',
    component: () => import('@/views/auth/AuthCallbackPage.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/validate-account',
    component: () => import('@/views/auth/ValidateAccountPage.vue'),
    meta: { requiresAuth: true }
  },
  // ============================================
  // Parent join family flow (multi-page)
  // ============================================
  {
    path: '/join',
    redirect: '/join/code'
  },
  // Le scanner QR a été retiré : rejoindre une famille, c'est saisir un code,
  // et un seul chemin vaut mieux qu'un chemin plus une caméra. La route reste
  // en redirection, les liens partagés et les anciens raccourcis la portent.
  {
    path: '/join/welcome',
    redirect: '/join/code'
  },
  {
    path: '/join/code',
    component: () => import('@/views/auth/ParentJoinCodePage.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/join/preview',
    component: () => import('@/views/auth/ParentJoinPreviewPage.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/join/auth',
    component: () => import('@/views/auth/ParentJoinAuthPage.vue'),
    meta: { requiresAuth: false }
  },
  // Rétro-compat : anciens chemins / deep-links éventuels → page unique OTP.
  { path: '/join/login', redirect: '/join/auth' },
  { path: '/join/register', redirect: '/join/auth' },
  {
    path: '/join/role',
    component: () => import('@/views/auth/ParentJoinRolePage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/join/success',
    component: () => import('@/views/auth/ParentJoinSuccessPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/join/error/connection',
    component: () => import('@/views/auth/ParentJoinConnectionErrorPage.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/tabs',
    component: () => import('@/views/TabsPage.vue'),
    children: [
      {
        path: '',
        redirect: HOME_PATH
      },
      {
        // L'ancien tableau de bord (jusqu'à la 2.6.x) : des notifications push
        // déjà parties portent `route: '/tabs/dashboard'`, et une notification
        // peut être ouverte plusieurs jours après son envoi. Elles atterrissent
        // sur l'accueil, qui en a repris le rôle.
        path: 'dashboard',
        redirect: HOME_PATH
      },
      {
        path: 'home',
        component: () => import('@/views/tabs/HomeTabPage.vue'),
        meta: { requiresAuth: true, requiresParent: true }
      },
      {
        path: 'tasks',
        component: () => import('@/views/tabs/TasksTabPage.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'rewards',
        component: () => import('@/views/tabs/RewardsTabPage.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'stats',
        component: () => import('@/views/stats/StatsPage.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'settings',
        component: () => import('@/views/tabs/SettingsTabPage.vue'),
        meta: { requiresAuth: true, requiresParent: true }
      },
      {
        path: 'settings/tasks',
        component: () => import('@/views/tasks/TasksPage.vue'),
        meta: { requiresAuth: true, requiresParent: true }
      },
      {
        path: 'settings/rewards',
        component: () => import('@/views/rewards/RewardsManagementPage.vue'),
        meta: { requiresAuth: true, requiresParent: true }
      },
      {
        // Same page as 'settings/rewards' but scoped to the Rewards tab so
        // re-tapping the Settings tab from here doesn't bring this page along.
        path: 'rewards/manage',
        component: () => import('@/views/rewards/RewardsManagementPage.vue'),
        meta: { requiresAuth: true, requiresParent: true }
      },
      {
        // Historique des remises — scoped au tab Récompenses (même mécanique que rewards/manage).
        path: 'rewards/history',
        component: () => import('@/views/rewards/RewardsHistoryPage.vue'),
        meta: { requiresAuth: true, requiresParent: true }
      },
      {
        // Same page as 'settings/tasks' but scoped to the Tasks tab so
        // re-tapping the Settings tab from here doesn't bring this page along.
        path: 'tasks/manage',
        component: () => import('@/views/tasks/TasksPage.vue'),
        meta: { requiresAuth: true, requiresParent: true }
      },
      {
        path: 'settings/community-guidelines',
        component: () => import('@/views/settings/CommunityGuidelinesPage.vue'),
        meta: { requiresAuth: true, requiresParent: true }
      },
      {
        path: 'settings/household-management',
        component: () => import('@/views/settings/HouseholdManagementPage.vue'),
        meta: { requiresAuth: true, requiresParent: true }
      },
      {
        path: 'settings/away-days',
        component: () => import('@/views/settings/AwayDaysSettingsPage.vue'),
        meta: { requiresAuth: true, requiresParent: true }
      },
      {
        path: 'settings/child-phones',
        component: () => import('@/views/settings/ChildPhonesSettingsPage.vue'),
        meta: { requiresAuth: true, requiresParent: true }
      },
      {
        path: 'settings/allowance',
        component: () => import('@/views/allowance/AllowanceSettingsPage.vue'),
        meta: { requiresAuth: true, requiresParent: true }
      },
      {
        path: 'settings/allowance/amounts',
        component: () => import('@/views/allowance/AllowanceChildAmountsPage.vue'),
        meta: { requiresAuth: true, requiresParent: true }
      },
      {
        path: 'settings/profile',
        component: () => import('@/views/settings/ProfilePage.vue'),
        meta: { requiresAuth: true, requiresParent: true }
      },
      {
        path: 'settings/notifications',
        component: () => import('@/views/settings/NotificationsPage.vue'),
        meta: { requiresAuth: true, requiresParent: true }
      },
      {
        path: 'settings/application',
        component: () => import('@/views/settings/ApplicationPage.vue'),
        meta: { requiresAuth: true, requiresParent: true }
      }
    ]
  },
  // Sas d'entrée des CTA emails. Sur device avec l'app installée, ce chemin
  // est intercepté en natif (Universal Link / App Links) et géré par App.vue.
  // Sur navigateur (desktop ou app absente), on charge la PWA ici et on
  // redirige vers la cible. Voir resolveGoTarget pour le mapping ?to=.
  {
    path: '/go',
    redirect: to =>
      resolveGoTarget(typeof to.query.to === 'string' ? to.query.to : null, {
        code: typeof to.query.code === 'string' ? to.query.code : null,
      }),
  },
  // Alias pour la compatibilité avec les anciens liens
  {
    path: '/app/:pathMatch(.*)*',
    redirect: to => {
      return { path: `/tabs/${to.params.pathMatch}` };
    }
  },
  // Routes pour les enfants - garder uniquement pour la page de connexion initiale
  // Les enfants utilisent maintenant l'interface parent (/tabs/*)
  {
    path: '/household',
    redirect: HOME_PATH
  },

  // ============================================
  // Child PIN Login (3-step flow)
  // ============================================
  {
    path: '/child',
    redirect: '/child/welcome'
  },
  {
    path: '/child/welcome',
    name: 'ChildJoinWelcome',
    component: () => import('@/views/child-app/ChildJoinWelcomePage.vue'),
    meta: { requiresAuth: false, isChildRoute: true }
  },
  {
    path: '/child/code',
    name: 'ChildPinLogin',
    component: () => import('@/views/child-app/ChildPinLoginPage.vue'),
    meta: { requiresAuth: false, isChildRoute: true }
  },
  {
    path: '/child/welcoming',
    name: 'ChildJoinWelcoming',
    component: () => import('@/views/child-app/ChildJoinWelcomingPage.vue'),
    meta: { requiresAuth: true, isChildRoute: true }
  },
  {
    path: '/child-mode',
    component: () => import('@/views/child-mode/ChildModeTabsPage.vue'),
    meta: { requiresAuth: true, requiresPremium: true },
    children: [
      { path: 'tasks', component: () => import('@/views/child-mode/ChildModeTasksTab.vue') },
      { path: 'rewards', component: () => import('@/views/child-mode/ChildModeRewardsTab.vue') },
      { path: 'allowance', component: () => import('@/views/child-mode/ChildModeAllowanceTab.vue') },
      // La journée de toute la famille, sur la tablette partagée seulement (le
      // verrou en ferme la porte au téléphone appairé, cf. childModeLock).
      { path: 'family', component: () => import('@/views/child-mode/ChildModeFamilyTab.vue') },
      // Onglet Bonus retiré jusqu'aux cartes « passer une tâche » (#399) : son
      // contenu est dans Récompenses.
      { path: 'bonus', redirect: '/child-mode/rewards' },
      { path: '', redirect: '/child-mode/tasks' },
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/welcome'
  }
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
});

// Navigation guard for authentication and household check
router.beforeEach(async (to, from, next) => {
  // console.log('🚦 [Router] beforeEach triggered:', {
  //   from: from.path,
  //     to: to.path,
  //       timestamp: new Date().toISOString()
  // });

  const authStore = useAuthStore();
  const childAuthStore = useChildAuthStore();
  const appContextStore = useAppContextStore();

  // ============================================
  // Check for child session on app start/reload
  // ============================================
  // If user is not on a child route and not authenticated as parent,
  // check if there's a valid child session (Supabase Auth) and redirect to child board
  const isChildRoute = to.matched.some(record => record.meta.isChildRoute);

  const isAuthPage = ['/welcome', '/login'].includes(to.path) ||
                     to.path.startsWith('/auth/');

  // ============================================
  // Child session detection (PIN auth)
  // ============================================
  // Try to restore child session if not already authenticated.
  // Must run even when authStore.user is set (child PIN creates a Supabase Auth user
  // that triggers onAuthStateChange, so authStore.user exists for child users too).
  if (!childAuthStore.isAuthenticated && !isAuthPage) {
    await childAuthStore.initSession()
    // Session enfant refermée parce que le foyer n'a plus Harmonia+ : on renvoie
    // l'enfant sur son écran de code, qui le lui explique avec ses mots. Sans ça
    // il finirait sur l'accueil parent (Rule 3), à se demander ce qu'il a cassé.
    if (childAuthStore.error === 'PREMIUM_REQUIRED' && !to.path.startsWith('/child/')) {
      next('/child/code')
      return
    }
  }

  // Fast path: child authenticated → only allow child-mode routes
  if (childAuthStore.isAuthenticated) {
    if (to.path.startsWith('/child-mode/') || isChildRoute || isAuthPage) {
      next();
    } else {
      next('/child-mode/tasks');
    }
    return;
  }

  // ============================================
  // Child Route Handling
  // ============================================
  const requiresChildAuth = to.matched.some(record => record.meta.requiresChildAuth);

  // Handle child routes separately
  if (isChildRoute && to.path.startsWith('/child')) {
    // Child pairing and profiles pages don't require auth
    if (to.path === '/child' || to.path === '/child/profiles' || to.path.startsWith('/child/')) {
      next();
      return;
    }

    // Child app routes require child authentication
    if (requiresChildAuth) {
      // First check if already authenticated
      if (!childAuthStore.isAuthenticated) {
        // Try to initialize session from Supabase Auth
        const hasSession = await childAuthStore.initSession();
        
        if (!hasSession) {
          // No valid child Supabase session, redirect to child login
          console.log('[Router] No child Supabase session found, redirecting to /child')
          next('/child');
          return;
        }
      }
      
      // Verify authentication is still valid
      if (!childAuthStore.isAuthenticated) {
        console.log('[Router] Child not authenticated after initSession, redirecting to /child')
        next('/child');
        return;
      }
    }

    // Allow navigation for authenticated child
    next();
    return;
  }

  // Attendre que l'authentification soit prête
  try {
    await authStore.waitForAuth();
  } catch (error) {
    console.error('[Router] Error waiting for auth:', error);
    next('/welcome');
    return;
  }

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth !== false);

  // --- Onboarding guard ---
  const { useOnboardingController } = await import('@/composables/useOnboardingController')
  const onboarding = useOnboardingController()
  await onboarding.init()

  // Un état d'onboarding peut survivre à un parcours pourtant terminé (auth
  // revenue par lien profond, finish() jamais appelé). Il prenait alors le pas
  // sur TOUT le reste de la garde, Rule 3b comprise : un utilisateur connecté,
  // avec foyer, restait planté sur /welcome à chaque lancement et croyait devoir
  // se reconnecter. Dès que le foyer existe, l'onboarding n'a plus lieu d'être :
  // on ignore l'état ici et on déclenche sa purge en tâche de fond.
  // ensureHouseholdsLoaded est caché (TTL 30 s), donc pas de requête par
  // navigation ; en onboarding réel l'utilisateur n'a pas de foyer et rien ne
  // change pour lui.
  let ghostOnboarding = false
  if (onboarding.isOnboarding.value && authStore.user) {
    await appContextStore.ensureHouseholdsLoaded(authStore.user.id)
    ghostOnboarding = appContextStore.availableHouseholds.length > 0
    if (ghostOnboarding) {
      void onboarding.discardGhostOnboarding()
    }
  }

  if (onboarding.isOnboarding.value && !ghostOnboarding) {
    const { useAppContextStore } = await import('@/stores/appContext')
    const appContextStore = useAppContextStore()
    const hasChildren = appContextStore.availableChildren.length > 0

    // Rule 1: Before first child is added, lock navigation to expected route
    // After first child, allow free navigation within /tabs/*
    const expected = onboarding.expectedRoute()
    if (!hasChildren && expected && to.path !== expected) {
      next(expected)
      return
    }
    // Rule 2: Bypass requiresAuth during onboarding (anonymous user may not exist yet)
    // Inject URL context params (hhId, childId) so task/reward pages load correctly
    const hhId = appContextStore.currentHouseholdId || onboarding.householdId.value
    if (to.path.startsWith('/tabs/') && !to.query.hhId && hhId) {
      const query: Record<string, string> = {
        ...to.query as Record<string, string>,
        hhId,
      }
      if (appContextStore.currentChildId) {
        query.childId = appContextStore.currentChildId
      }
      next({ path: to.path, query, replace: true })
      return
    }
    next()
    return
  }

  // Rule 3: Not authenticated + protected route → /welcome
  if (requiresAuth && !authStore.user) {
    next('/welcome');
    return;
  }

  // Rule 3d: mode enfant verrouillé (HARMONIA-87). Un parent a posé son
  // appareil devant ses enfants : rien ne sort de /child-mode sans le code
  // parent, que la navigation vienne d'un tap, d'une notification, d'un lien
  // /go, du retour Android ou d'une relance de l'app (le verrou est persisté).
  // La sortie légitime lève le verrou AVANT de naviguer (useExitChildMode).
  // La règle elle-même vit dans childModeLock.ts, testée à part. Le foyer est
  // chargé avant de juger les droits : au démarrage à froid les enfants ne sont
  // pas encore là, et un « aucun enfant n'y a droit » prématuré ouvrirait la
  // porte.
  if (appContextStore.childViewLocked || appContextStore.pairedDevice) {
    // Téléphone appairé dont le drapeau du verrou a disparu : on le remet.
    const paired = appContextStore.pairedDevice;
    if (paired && !appContextStore.childViewLocked) appContextStore.enterChildMode(paired.childId, { locked: true });
    let targetChildId: string | null = null;
    const pairedChildId = appContextStore.pairedDevice?.childId ?? null;
    let pairedChildAllowed: boolean | null = null;
    if (authStore.user) {
      await appContextStore.ensureHouseholdsLoaded(authStore.user.id);
      targetChildId = appContextStore.childModeTargetId(appContextStore.childViewChildId);
      // Un refus n'est établi que si l'enfant est bien chargé : un foyer absent
      // (hors ligne, chargement raté) ne doit pas déconnecter le téléphone.
      if (pairedChildId && appContextStore.availableChildren.some((c) => c.id === pairedChildId)) {
        pairedChildAllowed = appContextStore.childFeatureAllowed(pairedChildId);
      }
    }
    const lock = decideChildModeLock({
      locked: true,
      hasParentSession: !!authStore.user,
      targetChildId,
      currentChildId: appContextStore.childViewChildId,
      toPath: to.path,
      pairedChildId,
      pairedChildAllowed,
    });
    if (lock.action === 'unpair') {
      await authStore.logout();
      appContextStore.exitChildMode();
      next('/welcome');
      return;
    }
    if (lock.action === 'release') {
      appContextStore.exitChildMode();
    } else if (lock.action === 'stay' || lock.action === 'redirect') {
      if (lock.switchTo) appContextStore.enterChildMode(lock.switchTo, { locked: true });
      if (lock.action === 'redirect') {
        next(lock.to);
        return;
      }
    }
  }

  // Rule 3c: le mode enfant demande le profil ouvert ET la fonctionnalité :
  // dans un foyer `child_limit` le premier enfant y donne droit gratuitement,
  // dans un foyer `legacy` il demande l'abonnement comme il l'a toujours fait.
  // `childHasAccess` seul ne suffirait PAS : il répond toujours oui à un foyer
  // `legacy`, qui n'a pas de limite d'enfants, et rouvrirait le mode enfant à
  // un ancien foyer non abonné qui ne l'a jamais eu.
  // Les comptes admin (allowlist) passent quand même : l'accès admin à la vue
  // enfant doit fonctionner quel que soit le statut du foyer.
  const requiresPremium = to.matched.some(record => record.meta.requiresPremium === true);
  if (requiresPremium) {
    const { useAppContextStore } = await import('@/stores/appContext')
    const appContext = useAppContextStore()
    if (!appContext.childFeatureAllowed(appContext.childViewChildId)) {
      const { isAdminEmail } = await import('@/services/admin-access.service')
      if (!isAdminEmail(authStore.user?.email)) {
        next(HOME_PATH);
        return;
      }
    }
  }

  // Rule 3b: Authenticated + public auth page → redirect home
  // ⚠️ Tester la PRÉSENCE de la clé, pas sa valeur : `?logged-in-no-household`
  // (marqueur sans valeur) vaut `null` dans to.query → `!null === true` ferait croire
  // le param absent et renverrait vers l'accueil, créant une boucle infinie avec
  // Rule 4 (user sans foyer) → page blanche. Aligné sur le pattern `!== undefined`
  // déjà utilisé dans useAuthGuard.ts et OnboardingWelcomePage.vue. Cf. TW #17611466.
  const publicAuthPages = ['/welcome', '/login']
  const isNoHouseholdMarker = to.query['logged-in-no-household'] !== undefined
  if (authStore.user && publicAuthPages.includes(to.path) && !isNoHouseholdMarker) {
    next(HOME_PATH);
    return;
  }

  // Ancienne route /profile remplacée par une modale : rediriger vers l'accueil
  if (to.path === '/profile' && authStore.user) {
    next(HOME_PATH);
    return;
  }

  // ============================================
  // Check if route requires parent (e.g., Settings) – only redirect if we already know user is child
  // ============================================
  const requiresParent = to.matched.some(record => record.meta.requiresParent === true);
  const userContextStore = useUserContextStore();
  if (requiresParent && userContextStore.isChild === true) {
    console.log('[Router] Child user trying to access parent-only route, redirecting to tasks');
    next('/child-mode/tasks');
    return;
  }

  // Rule 4: Authenticated + 0 households + not in onboarding → /welcome?logged-in-no-household
  // ⚠️ EXEMPTER les chemins de RÉCUPÉRATION : un orphelin (authentifié sans foyer) atterrit
  // sur /welcome?logged-in-no-household avec les CTA « Créer » (/onboarding/*) et « Rejoindre »
  // (/join/*). Sans cette exemption, Rule 4 renverrait ces chemins vers /welcome → l'orphelin
  // serait PIÉGÉ (clic « Rejoindre » → rebond sur welcome, « rien ne se passe »). « Créer »
  // marchait via le bypass isOnboarding ; « Rejoindre » (/join, sans isOnboarding) était bloqué.
  // Cf. TW #17611466.
  const isRecoveryPath = to.path.startsWith('/join/') || to.path.startsWith('/onboarding/')
  if (authStore.user && to.path !== '/welcome' && !isRecoveryPath && (!onboarding.isOnboarding.value || ghostOnboarding)) {
    // Ensure households are loaded before checking (prevents false redirect on cold start)
    await appContextStore.ensureHouseholdsLoaded(authStore.user.id)
    if (appContextStore.availableHouseholds.length === 0) {
      // Allow child users to proceed to tasks
      await userContextStore.loadUserContext()
      if (userContextStore.isChild) {
        if (to.path.startsWith('/child-mode/')) {
          next()
        } else {
          next('/child-mode/tasks')
        }
        return
      }
      next('/welcome?logged-in-no-household')
      return
    }
  }

  // Nettoyer le provider temporaire si l'utilisateur a des households (connexion réussie)
  if (authStore.user && appContextStore.availableHouseholds.length > 0) {
    localStorage.removeItem('harmonia_oauth_provider');
  }

  // Hydrate the whole household before entering the tabs, so a RUNTIME login/
  // signup lands on warm stores (instant nav), exactly like the cold-boot path
  // does before the splash hides. This is the single chokepoint every post-auth
  // flow funnels through (the landing redirects are scattered across ~8 pages).
  // hydrateHousehold is idempotent per household, so repeat /tabs navigations
  // (and the cold-boot case, already hydrated in App.vue) short-circuit for free.
  // Bounded by a timeout so a stalled query can't wedge navigation.
  if (
    authStore.user &&
    to.path.startsWith('/tabs/') &&
    appContextStore.currentHouseholdId
  ) {
    try {
      const { hydrateHousehold } = await import('@/services/householdHydration');
      await Promise.race([
        hydrateHousehold(appContextStore.currentHouseholdId),
        new Promise<void>((resolve) => setTimeout(resolve, 4000)),
      ]);
    } catch (e) {
      console.error('[Router] Household hydration before tabs failed:', e);
    }
  }

  next();
});

router.afterEach((to) => {
  perfIncrement('routeChanges');
  perfMark(`route-${to.path.replace(/\//g, '_') || 'root'}`);

  // Le résumé de perf ne part QUE en dev (ou sonde active). En production il
  // tournait à chaque navigation, et il coûtait cher pour rien :
  // - il JSON.stringify un tableau de marks qui GRANDIT à chaque route (22, 23,
  //   24 entrées… il n'est jamais purgé) ;
  // - il émet deux console.log, dont un multi-ligne ;
  // - l'intégration console de Sentry transforme chacun en addBreadcrumb, soit
  //   un aller-retour de pont Capacitor vers le natif, transportant tout le
  //   payload sérialisé.
  // Mesuré sur device : c'était l'un des postes de travail thread principal à
  // chaque bascule d'onglet. perfMark reste actif, il est quasi gratuit et
  // alimente encore la sonde.
  if (import.meta.env.DEV || isTabProbeEnabled()) {
    perfLogSummary(`route:${to.path}`);
    perfLogConclusion(to.path);
  }
});

// Transitions de page, poussée et retour. Installées EN DERNIER pour passer
// APRÈS la garde d'authentification : quand celle-ci détourne une navigation,
// la nôtre ne s'exécute pas sur celle qui vient d'être abandonnée, mais sur
// celle qui la remplace.
installPageTransitions(router);

export default router;
